import React, { useState, useEffect, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import SidePanel from './components/SidePanel';
import Preview from './components/Preview';
import DeleteModal from './components/DeleteModal'
import chatApi from '../../api/chatApi'
import ledgerAgentApi from '../../api/ledgerAgentApi'
import type { LedgerAgentHistorySession, LedgerAgentSessionMessage } from '../../api/ledgerAgentApi'
import type { ChatMessage } from '../../api/types'
import { useIsMobile } from '../../utils/hooks'
import { useQuery } from '@tanstack/react-query';
import userMetadataApi from "../../api/userMetadataApi"
import { useWorkspace } from '../../context/WorkspaceContext'
import SearchableBusinessSelect from '../../components/SearchableBusinessSelect';

type HistoryConversationListItem = {
    conversation_id: string;
    preview: string;
    last_updated: string;
    source?: 'chat' | 'ledger_agent';
};

type HistoryConversationMessage = {
    role: 'human' | 'ai';
    content: string;
};

type HistoryConversationDetail = {
    conversation_id: string;
    title: string;
    messages: HistoryConversationMessage[];
    source?: 'chat' | 'ledger_agent';
};

const HistoryPage: React.FC = () => {
    const [conversations, setConversations] = useState<HistoryConversationListItem[]>([]);
    const [selectedId, setSelectedId] = useState<string | null>(null);
    const [selectedConversation, setSelectedConversation] = useState<HistoryConversationDetail | null>(null);
    const [deleteTarget, setDeleteTarget] = useState<{ id: string; title: string } | null>(null);
    const [animatingOut, setAnimatingOut] = useState(false);
    const [isLoadingConversation, setIsLoadingConversation] = useState(false);
    const [searchQuery, setSearchQuery] = useState('');
    const [debouncedSearchQuery, setDebouncedSearchQuery] = useState('');
    const conversationListRequestRef = useRef(0);
    const conversationDetailRequestRef = useRef(0);

    const navigate = useNavigate()
    const location = useLocation();
    const isMobile = useIsMobile();
    const isAdminEmbedded = location.pathname.startsWith('/admin/business/');
    const { workspaces, activeBusinessId, setActiveBusinessId } = useWorkspace();

    // Admin Business Selector Logic
    const { data: userMetadata } = useQuery({
        queryKey: ['userMetadata'],
        queryFn: () => userMetadataApi.getUserMetadata(),
        staleTime: Infinity,
    });
    const isAdmin = userMetadata?.role === 'admin';

    const [selectedBusinessId, setSelectedBusinessId] = useState<string>('');

    const userBusinessOptions = Array.from(
        new Map(
            workspaces.flatMap((workspace) =>
                (workspace.businesses || []).map((business) => [
                    business.id,
                    {
                        id: business.id,
                        name: business.legal_name || business.dba_name || 'Unnamed Business',
                    },
                ]),
            ),
        ).values(),
    );
    const { data: businessesData } = useQuery({
        queryKey: ['allBusinesses'],
        queryFn: userMetadataApi.getAllBusinesses,
        enabled: isAdmin,
    });
    const adminBusinessOptions = (businessesData?.businesses || []).map((b: any) => ({
        id: b.business_id || b.user_id,
        name: b.legal_name || b.dba_name || b.onboarding?.qbo_metadata?.company_name || b.user_email || 'Unknown',
        subtitle: b.user_email || undefined,
    }));
    const businessOptions = isAdmin ? adminBusinessOptions : userBusinessOptions;

    useEffect(() => {
        if (activeBusinessId) {
            setSelectedBusinessId(activeBusinessId);
        }
    }, [activeBusinessId]);

    const effectiveBusinessId = activeBusinessId || undefined;

    useEffect(() => {
        conversationListRequestRef.current += 1;
        conversationDetailRequestRef.current += 1;
        setConversations([]);
        setSelectedId(null);
        setSelectedConversation(null);
        setDeleteTarget(null);
    }, [effectiveBusinessId]);

    useEffect(() => {
        const timeout = setTimeout(() => {
            setDebouncedSearchQuery(searchQuery.trim());
        }, 300);
        return () => clearTimeout(timeout);
    }, [searchQuery]);

    const handleDelete = (id: string) => {
        void deleteConversation(id);
    }

    const handleEdit = (id: string, newTitle: string) => {
        updateConversation(id, { title: newTitle });
        // alert(`changed conversation id: ${id} title to "${newTitle}"`) // TEST
    }

    const handleConversationSelect = (id: string) => {
        if (id === sessionStorage.getItem("conversation_id")) {
            navigate('/home')
        } else {
            // Set this IMMEDIATELY to trigger the slide animation
            setSelectedId(id);
            fetchConversation(id);
        }
    }



    const handleCloseMobilePreview = () => {
        conversationDetailRequestRef.current += 1;
        setAnimatingOut(true);
        setSelectedId(null);
        setIsLoadingConversation(false);
        setTimeout(() => {
            setSelectedConversation(null);
            setAnimatingOut(false);
        }, 300); // Wait for CSS transition to complete
    };



    // API Helper Functions
    const fetchConversations = async (search?: string) => {
        const requestId = ++conversationListRequestRef.current;
        try {
            const [chatData, ledgerAgentData] = await Promise.all([
                chatApi.listConversations(effectiveBusinessId, search),
                ledgerAgentApi.listSessions(effectiveBusinessId, search),
            ]);
            if (requestId !== conversationListRequestRef.current) return;
            const combined = [
                ...chatData.map((item) => ({ ...item, source: 'chat' as const })),
                ...ledgerAgentData.sessions.map((item: LedgerAgentHistorySession) => ({
                    conversation_id: item.session_id,
                    preview: item.preview,
                    last_updated: item.last_updated,
                    source: 'ledger_agent' as const,
                })),
            ].sort((a, b) => new Date(b.last_updated).getTime() - new Date(a.last_updated).getTime());
            setConversations(combined);
        } catch (error) {
            console.error("Failed to fetch list of conversations:", error);
        }
    };
    const fetchConversation = async (conversation_id: string) => {
        const requestId = ++conversationDetailRequestRef.current;
        setIsLoadingConversation(true);
        try {
            const selectedMeta = conversations.find((conversation) => conversation.conversation_id === conversation_id);
            if (selectedMeta?.source === 'ledger_agent') {
                const data = await ledgerAgentApi.getSession(conversation_id);
                if (requestId !== conversationDetailRequestRef.current) return;
                setSelectedConversation({
                    conversation_id,
                    title: data.session.title || 'Ledger Agent',
                    source: 'ledger_agent',
                    messages: (data.session.messages || []).map((message: LedgerAgentSessionMessage) => ({
                        role: message.role === 'user' ? 'human' : 'ai',
                        content: message.text,
                    })),
                });
            } else {
                const data = await chatApi.getConversation(conversation_id, effectiveBusinessId);
                if (requestId !== conversationDetailRequestRef.current) return;
                setSelectedConversation({
                    conversation_id: data.conversation_id,
                    title: data.title,
                    source: 'chat',
                    messages: (data.messages || []).map((message: ChatMessage) => ({
                        role: message.role === 'human' ? 'human' : 'ai',
                        content: typeof message.content === 'string' ? message.content : message.content?.text || '',
                    })),
                });
            }
        } catch (error) {
            console.error("Failed to fetch conversation:", error);
            if (requestId === conversationDetailRequestRef.current) {
                setSelectedId(null); // Reset if it fails so it slides back
            }
        } finally {
            if (requestId === conversationDetailRequestRef.current) {
                setIsLoadingConversation(false);
            }
        }
    };

    const updateConversation = async (
        conversation_id: string,
        update: { title?: string | null; is_deleted?: boolean | null }
    ) => {
        try {
            // Optimistic Update for Deletion
            if (update.is_deleted) {
                setConversations(prev => prev.filter(c => c.conversation_id !== conversation_id));
                if (selectedId === conversation_id) {
                    setSelectedId(null);
                    setSelectedConversation(null);
                }
            }

            await chatApi.updateConversation(conversation_id, update, effectiveBusinessId);
            await fetchConversations(debouncedSearchQuery); // Refresh list to ensure sync

            // Only refresh preview if it's NOT deleted and currently selected
            if (!update.is_deleted && selectedId === conversation_id) {
                await fetchConversation(conversation_id);
            }
        } catch (error) {
            console.error("Failed to update conversation:", error);
            // Revert optimistic update if needed? For now, fetchConversations will restore truth
            fetchConversations(debouncedSearchQuery);
        }
    };

    const deleteConversation = async (conversation_id: string) => {
        try {
            const selectedMeta = conversations.find((conversation) => conversation.conversation_id === conversation_id);
            if (selectedMeta?.source === 'ledger_agent') return;
            setConversations(prev => prev.filter(c => c.conversation_id !== conversation_id));
            if (selectedId === conversation_id) {
                setSelectedId(null);
                setSelectedConversation(null);
            }

            await chatApi.deleteConversation(conversation_id, effectiveBusinessId);
            await fetchConversations(debouncedSearchQuery);
        } catch (error) {
            console.error("Failed to delete conversation:", error);
            fetchConversations(debouncedSearchQuery);
        }
    };

    // WHEN API IS WORKING ----------------------------------------
    useEffect(() => {
        if (!effectiveBusinessId) return;
        const loadConversations = async () => {
            const requestId = ++conversationListRequestRef.current;
            try {
                const [chatData, ledgerAgentData] = await Promise.all([
                    chatApi.listConversations(effectiveBusinessId, debouncedSearchQuery),
                    ledgerAgentApi.listSessions(effectiveBusinessId, debouncedSearchQuery),
                ]);
                if (requestId !== conversationListRequestRef.current) return;
                const combined = [
                    ...chatData.map((item) => ({ ...item, source: 'chat' as const })),
                    ...ledgerAgentData.sessions.map((item: LedgerAgentHistorySession) => ({
                        conversation_id: item.session_id,
                        preview: item.preview,
                        last_updated: item.last_updated,
                        source: 'ledger_agent' as const,
                    })),
                ].sort((a, b) => new Date(b.last_updated).getTime() - new Date(a.last_updated).getTime());
                setConversations(combined);
            } catch (error) {
                console.error("Failed to fetch list of conversations:", error);
            }
        };
        loadConversations();
    }, [effectiveBusinessId, debouncedSearchQuery]); // Re-fetch when business/search changes

    useEffect(() => {
        if (isMobile) return;
        if (conversations.length === 0) {
            setSelectedId(null);
            setSelectedConversation(null);
            return;
        }

        const selectedStillExists = selectedId && conversations.some(
            (conversation) => conversation.conversation_id === selectedId
        );

        if (selectedStillExists) {
            return;
        }

        const latestConversation = conversations[0];
        setSelectedId(latestConversation.conversation_id);
        fetchConversation(latestConversation.conversation_id);
    }, [conversations, selectedId, isMobile]);


    const handleShowModal = (id: string, title: string) => {
        setDeleteTarget({ id, title });
    };

    return (
        <div className="flex flex-col h-full w-full">
            {!isAdminEmbedded && businessOptions.length > 1 && (
                <div className="bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between flex-shrink-0">
                    <div className="flex items-center gap-4">
                        <h1 className="text-2xl font-bold text-gray-900">History</h1>
                        <SearchableBusinessSelect
                            options={businessOptions}
                            value={activeBusinessId || selectedBusinessId}
                            onChange={setActiveBusinessId}
                            widthClassName="w-80"
                            showSubtitleWhenClosed={false}
                        />
                    </div>
                </div>
            )}
            <div className="flex-1 overflow-hidden relative w-full">
                {isMobile ? (
                    <div className="relative h-full w-full">
                        {/* List panel - always rendered */}
                        <div className="h-full w-full overflow-hidden p-3">
                            <div className="flex h-full min-h-0 flex-col overflow-hidden rounded-lg border bg-white">
                                <SidePanel chats={conversations} selectedId={selectedId} onSelect={handleConversationSelect}
                                    handleShowModal={handleShowModal} onEdit={handleEdit} searchQuery={searchQuery} onSearchChange={setSearchQuery} readOnly={isAdmin} />
                            </div>
                        </div>

                        {/* Chat preview panel - slides in from the right as an overlay */}
                        <div
                            className={`absolute inset-0 bg-white flex flex-col transition-transform duration-300 ease-in-out ${(selectedId || animatingOut) ? 'translate-x-0' : 'translate-x-full'
                                }`}
                        >
                            {/* Back button */}
                            <div className="border-b px-4 py-3 flex items-center bg-gray-50 flex-shrink-0">
                                <button
                                    onClick={handleCloseMobilePreview}
                                    className="text-gray-600 font-medium bg-white px-3 py-1.5 border rounded shadow-sm flex items-center gap-1 active:bg-gray-100"
                                >
                                    &larr; Back
                                </button>
                            </div>
                            <div className="flex-1 overflow-hidden relative min-h-0">
                                <Preview selectedConversation={selectedConversation} selectedId={selectedId} readOnly={isAdmin} animatingOut={animatingOut} />
                            </div>
                        </div>
                    </div>
                ) :
                    <div className="flex h-full w-full">
                        <div className="w-[320px] shrink-0 border-r">
                            <SidePanel chats={conversations} selectedId={selectedId} onSelect={handleConversationSelect}
                                handleShowModal={handleShowModal} onEdit={handleEdit} searchQuery={searchQuery} onSearchChange={setSearchQuery} readOnly={isAdmin} />
                        </div>
                        <div className="flex-1">
                            <Preview selectedConversation={selectedConversation} readOnly={isAdmin} />
                        </div>
                    </div>
                }
            </div>

            {deleteTarget && (
                <DeleteModal
                    chatTitle={deleteTarget.title}
                    onCancel={() => setDeleteTarget(null)}
                    onConfirm={() => {
                        handleDelete(deleteTarget.id);
                        setDeleteTarget(null);
                    }}
                />
            )}
        </div>
    )
};

export default HistoryPage;
